import { useEffect, useRef, useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { AffiliatesService, type AffiliateRecord } from '@/services/affiliates'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogBody,
    DialogFooter,
} from '@/components/ui/dialog'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import toastService from '@/services/toast/service'

interface AttributeAffiliateModalProps {
    agencyId: string
    agencyName: string
    /** The signup code that matched no affiliate; the new referral replaces it */
    unmatchedCode?: string | null
    open: boolean
    onOpenChange: (open: boolean) => void
    onSuccess: () => void
}

/**
 * Attributes the agency to an affiliate picked from the Affonso roster.
 *
 * The roster search runs server-side, so cmdk's own filtering is switched off
 * (same as AgencyCombobox).
 */
export function AttributeAffiliateModal({ agencyId, agencyName, unmatchedCode, open, onOpenChange, onSuccess }: AttributeAffiliateModalProps) {
    const [search, setSearch] = useState('')
    const [affiliates, setAffiliates] = useState<AffiliateRecord[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [selected, setSelected] = useState<AffiliateRecord | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const requestRef = useRef(0)

    useEffect(() => {
        if (!open) return
        const requestId = ++requestRef.current
        setIsLoading(true)
        const timer = setTimeout(async () => {
            try {
                const res = await AffiliatesService.getAll(1, 20, search)
                // A slower earlier request must not overwrite a newer one's results.
                if (requestId === requestRef.current) setAffiliates(res.affiliates)
            } catch {
                if (requestId === requestRef.current) setAffiliates([])
            } finally {
                if (requestId === requestRef.current) setIsLoading(false)
            }
        }, 250)
        return () => clearTimeout(timer)
    }, [search, open])

    const handleOpenChange = (next: boolean) => {
        if (!next) {
            setSearch('')
            setSelected(null)
        }
        onOpenChange(next)
    }

    const handleSubmit = async () => {
        if (!selected) return
        setIsSubmitting(true)

        try {
            await AffiliatesService.attributeAgency(selected.id, agencyId)
            toastService.success('Affiliate attributed')
            onSuccess()
            handleOpenChange(false)
        } catch (err: any) {
            // The api client already toasts server error messages
            if (!err.response?.data?.message) {
                toastService.error("Couldn't attribute affiliate")
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle>{unmatchedCode ? 'Reattribute to Affiliate' : 'Attribute to Affiliate'}</DialogTitle>
                    <DialogDescription>
                        {unmatchedCode && <>{agencyName} signed up with the code "{unmatchedCode}", which matches no affiliate. </>}
                        Credit {agencyName} as a referral of the affiliate you pick. The referral is created under the owner's email.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody>
                    <Command shouldFilter={false} className="rounded-md border">
                        <CommandInput
                            placeholder="Search affiliates by name or email"
                            value={search}
                            onValueChange={setSearch}
                            disabled={isSubmitting}
                            autoFocus
                        />
                        <CommandList className="max-h-[260px]">
                            {isLoading ? (
                                <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Searching
                                </div>
                            ) : (
                                <>
                                    <CommandEmpty>No affiliates match that search</CommandEmpty>
                                    <CommandGroup>
                                        {affiliates.map((affiliate) => (
                                            <CommandItem
                                                key={affiliate.id}
                                                value={affiliate.id}
                                                disabled={isSubmitting}
                                                onSelect={() => setSelected(affiliate)}
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <div className="truncate">{affiliate.name}</div>
                                                    <div className="truncate text-xs text-muted-foreground">
                                                        {affiliate.email}
                                                    </div>
                                                </div>
                                                <Check
                                                    className={cn(
                                                        'ml-2 h-4 w-4 shrink-0',
                                                        selected?.id === affiliate.id ? 'opacity-100' : 'opacity-0'
                                                    )}
                                                />
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </>
                            )}
                        </CommandList>
                    </Command>
                </DialogBody>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button type="button" onClick={handleSubmit} disabled={isSubmitting || !selected}>
                        {isSubmitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Attributing...
                            </>
                        ) : selected ? (
                            `Attribute to ${selected.name}`
                        ) : (
                            'Attribute'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
